import { useEffect, useState } from "react";
import { useRepoStatus, useSession } from "../data/usePage.js";
import { useRepo } from "../data/repo.js";
import {
  pullRepo,
  pushRepo,
  rebaseRepo,
  backupRepo,
  resetRepo,
  gitTokenStatus,
  createGitToken,
  revokeGitToken,
} from "../api.js";
import { invalidate } from "../data/useFetch.js";
import { SystemPage, SystemSection } from "./SystemPage.js";
import type { RepoStatus } from "../../shared/types.js";

function timeAgo(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function RepoView() {
  const repo = useRepoStatus();
  const session = useSession();

  if (repo.status === "loading") return null;
  if (repo.status === "error")
    return <pre className="dw-error">{repo.error.message}</pre>;

  // A read-only repo has nothing to pull into or push from — every write is
  // capped away server-side — so it gets no git actions, and no note about
  // why: it simply isn't an option here. Clone stays; reading is the point.
  const editing =
    session.status === "ready" && session.data.editing && !repo.data.readOnly;
  return (
    <SystemPage
      title={repo.data.name}
      lead={
        <>
          <span className="dw-code">⎇ {repo.data.branch}</span>
          {repo.data.remoteUrl && (
            <>
              {" · "}
              <span className="dw-muted">{repo.data.remoteUrl}</span>
            </>
          )}
          {repo.data.local && <> · local</>}
        </>
      }
    >
      <Sync repo={repo.data} editing={editing} />
      {repo.data.hosted && repo.data.cloneUrl && (
        <Clone cloneUrl={repo.data.cloneUrl} signedIn={Boolean(session.status === "ready" && session.data.user)} />
      )}
      <Worktrees repo={repo.data} />
    </SystemPage>
  );
}

/** Clone instructions for a datawiki-hosted repo, plus the per-user git token
 * (cross-repo; clone/push auth). See docs/git/Hosting.md. */
function Clone({ cloneUrl, signedIn }: { cloneUrl: string; signedIn: boolean }) {
  const [hasToken, setHasToken] = useState<boolean | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!signedIn) return;
    gitTokenStatus()
      .then((s) => setHasToken(s.set))
      .catch(() => setHasToken(false));
  }, [signedIn]);

  const create = async (): Promise<void> => {
    setBusy(true);
    try {
      const { token } = await createGitToken();
      setToken(token);
      setHasToken(true);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (): Promise<void> => {
    setBusy(true);
    try {
      await revokeGitToken();
      setToken(null);
      setHasToken(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SystemSection title="Clone">
      <p className="dw-muted">datawiki hosts this repo — clone it directly:</p>
      <pre className="dw-cmd-inline">
        <code>git clone {cloneUrl}</code>
      </pre>
      {!signedIn ? (
        <p className="dw-muted">Sign in to create a git token for pushing.</p>
      ) : (
        <>
          <p className="dw-muted">
            Authenticate git with a personal token (works for every hosted repo):
            use it as the password, any username.
          </p>
          {token && (
            <pre className="dw-cmd-inline">
              <code>{token}</code>
              <span className="dw-muted"> — copy it now; it isn't shown again.</span>
            </pre>
          )}
          <div className="dw-repo-actions">
            <button type="button" disabled={busy} onClick={create}>
              {hasToken ? "Regenerate token" : "Create token"}
            </button>
            {hasToken && (
              <button
                type="button"
                disabled={busy}
                className="dw-link dw-danger-link"
                onClick={revoke}
              >
                Revoke
              </button>
            )}
          </div>
        </>
      )}
    </SystemSection>
  );
}

type SyncAction = "push" | "pull" | "rebase" | "backup" | "reset";

function Sync({ repo, editing }: { repo: RepoStatus; editing: boolean }) {
  const repoName = useRepo();
  const [busy, setBusy] = useState<SyncAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which action produced `error`, so the failure marks its own button rather
  // than only appearing as loose text at the bottom of the section.
  const [failed, setFailed] = useState<SyncAction | null>(null);

  const run = async (action: SyncAction): Promise<void> => {
    setBusy(action);
    setError(null);
    setFailed(null);
    try {
      if (action === "push") await pushRepo(repoName);
      else if (action === "pull") await pullRepo(repoName);
      else if (action === "rebase") await rebaseRepo(repoName);
      else if (action === "backup") await backupRepo(repoName);
      else await resetRepo(repoName);
      invalidate("repo");
      // Anything that moves main (not push/backup) changes the served tree.
      if (action !== "push" && action !== "backup") {
        invalidate("tree");
        invalidate("page:");
        invalidate("commit");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setFailed(action);
    } finally {
      setBusy(null);
    }
  };

  const head = repo.head;
  return (
    <SystemSection title="Commit">
      {head ? (
        <p className="dw-repo-head">
          <code>{head.hash}</code> {head.message || "(no message)"}
          <br />
          <span className="dw-muted">
            {head.author} · <span title={head.date}>{timeAgo(head.date)}</span>
          </span>
        </p>
      ) : (
        <p className="dw-muted">No commits yet.</p>
      )}

      <p className="dw-repo-drift">
        {repo.remoteUrl ? (
          <>
            {repo.ahead} ahead · {repo.behind} behind origin/{repo.branch}
            <br />
            <span className="dw-muted">
              {repo.pollSeconds > 0
                ? `auto-syncs every ${repo.pollSeconds}s`
                : "auto-sync off"}
              {repo.lastRemoteCheckAt && (
                <> · remote checked {timeAgo(repo.lastRemoteCheckAt)}</>
              )}
            </span>
          </>
        ) : (
          <span className="dw-muted">No remote configured.</span>
        )}
      </p>

      {editing ? (
        <div className="dw-repo-actions">
          <button
            type="button"
            className="primary"
            disabled={busy !== null || !repo.remoteUrl}
            onClick={() => run("pull")}
          >
            {busy === "pull" ? "Pulling…" : "Pull"}
          </button>
          <button
            type="button"
            disabled={busy !== null || !repo.canPush || repo.ahead === 0}
            onClick={() => run("push")}
            title={
              !repo.canPush
                ? "GitHub is not connected"
                : repo.ahead === 0
                  ? "Nothing to push"
                  : "Push to origin"
            }
          >
            {busy === "push" ? "Pushing…" : "Push"}
          </button>
        </div>
      ) : (
        // Read-only: no actions and no explanation — it isn't a thing you could
        // do here. Otherwise the reason is that you're signed out, which is
        // worth saying because signing in fixes it.
        !repo.readOnly && <p className="dw-muted">Sign in to push or pull.</p>
      )}
      {editing && repo.diverged && (
        <div className="dw-repo-diverged">
          <p className="dw-error">
            main and origin/{repo.branch} have diverged ({repo.ahead} local ·{" "}
            {repo.behind} remote) — a plain pull can't fast-forward. Recover:
          </p>
          <ul className="dw-muted dw-repo-recover-help">
            <li>
              <strong>Rebase onto origin</strong> — replay the local commits on
              top of origin, then push. Aborts on conflict (nothing lost).
            </li>
            <li>
              <strong>Back up to origin</strong> — push the local commits to a{" "}
              <code>dw-main-*</code> branch so they can't be lost.
            </li>
            <li>
              <strong>Reset to origin</strong> — discard the local commits and
              mirror origin. Unlocks only once a backup exists.
            </li>
          </ul>
          <div className="dw-repo-actions">
            <button
              type="button"
              className={failed === "rebase" ? "dw-btn-failed" : "primary"}
              disabled={busy !== null}
              title={
                failed === "rebase"
                  ? "The rebase hit a conflict and was aborted — nothing changed"
                  : "Replay the local commits on top of origin, then push"
              }
              onClick={() => run("rebase")}
            >
              {busy === "rebase"
                ? "Rebasing…"
                : failed === "rebase"
                  ? "⚠ Rebase failed — retry"
                  : "Rebase onto origin"}
            </button>
            <button
              type="button"
              disabled={busy !== null || !repo.canPush}
              onClick={() => run("backup")}
              title={
                repo.backupRef
                  ? `Backed up to ${repo.backupRef}`
                  : "Push local commits to a dw-main-* branch"
              }
            >
              {busy === "backup"
                ? "Backing up…"
                : repo.backupRef
                  ? `Backed up (${repo.backupRef})`
                  : "Back up to origin"}
            </button>
            <button
              type="button"
              className="dw-danger-link"
              disabled={busy !== null || !repo.backupRef}
              title={
                repo.backupRef
                  ? `Discard local commits, mirror origin/${repo.branch}`
                  : "Back up to origin first"
              }
              onClick={() => {
                if (
                  window.confirm(
                    `Discard the ${repo.ahead} local commit(s) and reset main to origin/${repo.branch}? They remain on ${repo.backupRef}.`,
                  )
                )
                  run("reset");
              }}
            >
              {busy === "reset" ? "Resetting…" : "Reset to origin"}
            </button>
          </div>
          {failed === "rebase" && (
            <p className="dw-error dw-repo-rebase-failed">
              The rebase hit a conflict and was aborted — main is exactly as it
              was, nothing is lost. The two histories touch the same lines, so
              they have to be reconciled by hand: <strong>Back up to origin</strong>{" "}
              first, then follow the steps that appear below.
            </p>
          )}
          {repo.backupRef && (
            <div className="dw-repo-recover">
              <p>
                The local commits are safe on <code>{repo.backupRef}</code>. To
                keep <em>both</em> sides, reconcile them in an ordinary clone —
                replay origin/{repo.branch} on top of the backup, so this
                checkout can fast-forward onto the result:
              </p>
              <pre>
                {[
                  `git clone ${repo.remoteUrl ?? "<remote>"} recover`,
                  `cd recover`,
                  `git checkout -B reconcile origin/${repo.branch}`,
                  `git rebase origin/${repo.backupRef}`,
                  `git push --force-with-lease origin reconcile:${repo.branch}`,
                ].join("\n")}
              </pre>
              <p>
                Then come back here and press <strong>Pull</strong> — origin/
                {repo.branch} now descends from this checkout's HEAD, so it
                fast-forwards.
              </p>
              <p className="dw-muted">
                That last push rewrites origin/{repo.branch}. Anyone else who
                has it checked out will need to reset onto the new tip. To avoid
                the rewrite, <code>git merge origin/{repo.backupRef}</code>{" "}
                instead of rebasing and push normally — same fast-forward here,
                at the cost of a merge commit. Either way, delete{" "}
                <code>{repo.backupRef}</code> once you've confirmed the result.
              </p>
              <p className="dw-muted">
                Full walkthrough: <code>docs/git/Recovery.md</code>.
              </p>
            </div>
          )}
        </div>
      )}
      {error && <pre className="dw-error">{error}</pre>}
    </SystemSection>
  );
}

function Worktrees({ repo }: { repo: RepoStatus }) {
  return (
    <SystemSection title={`Active worktrees (${repo.worktrees.length})`}>
      {repo.local ? (
        <p className="dw-muted">
          Local mode — you edit the working tree directly, no per-editor
          worktrees.
        </p>
      ) : repo.worktrees.length === 0 ? (
        <p className="dw-muted">No one is editing right now.</p>
      ) : (
        <table className="dw-repo-worktrees">
          <thead>
            <tr>
              <th>branch</th>
              <th>ahead</th>
              <th>behind</th>
              <th>changed</th>
              <th>sync</th>
            </tr>
          </thead>
          <tbody>
            {repo.worktrees.map((w) => (
              <tr key={w.branch}>
                <td>
                  <code>{w.branch}</code>
                </td>
                <td>{w.ahead}</td>
                <td>{w.behind}</td>
                <td>{w.changed}</td>
                <td>
                  {w.sync?.status === "conflict" ? (
                    <span className="dw-git-sync-conflict" title={w.sync.error ?? undefined}>
                      conflict
                    </span>
                  ) : w.behind > 0 ? (
                    <span className="dw-muted">pending</span>
                  ) : (
                    <span className="dw-muted">ok</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SystemSection>
  );
}
